import { useEffect, useState } from "react";
import Tooltip from "@kea-mod/common/tooltip";
import { Assessment } from "@kea-mod/common/types/Assessment";
import { useTranslation } from "react-i18next";

type Props = {
  activeAssessment: Assessment;
  // Receives the entered points as an absolute value.
  h: (value: string) => void;
  multiple: Boolean;
};

// An entry in progress can be in a state that is not a complete number yet ("", "-", "4.").
// Those states have to survive a render, otherwise the character that was just typed would
// be taken away again by the synchronisation below.
const isPartialInput = (value: string) => value === "" || value === "-" || value === "+" || value.endsWith(".");

export const TotalscoreField = (props: Props) => {
  const { t } = useTranslation();
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const maxNum = parseFloat(props.activeAssessment.maxTotalScore);
  const hasValidMax = !isNaN(maxNum) && maxNum > 0;
  const storedNum = parseFloat(props.activeAssessment.totalscore);

  // Points are entered and shown absolutely. A single assessment keeps the score as a
  // factor of the maximum, so the stored value is scaled up for the display.
  const storedAsPoints = props.multiple ? storedNum : storedNum * maxNum;
  const displayPoints = isNaN(storedAsPoints) ? "" : (Math.round(storedAsPoints * 100) / 100).toString();

  const [draft, setDraft] = useState(displayPoints);

  // Values that were set from the outside (calculation aid, changed maximum, initial load)
  // are taken over, but only while no entry is in progress.
  useEffect(() => {
    if (isPartialInput(draft)) return;
    if (parseFloat(draft) === parseFloat(displayPoints)) return;
    setDraft(displayPoints);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPoints]);

  // Without a maximum the entered points cannot be converted into the stored factor, so
  // the field stays closed until a maximum total score exists.
  const inputBlocked = !props.multiple && !hasValidMax;

  const onChange = (e: any) => {
    // Accept comma as a decimal separator by normalising it to a period.
    const raw = e.target.value.replace(",", ".");

    if (!/^[+\-]?\d*\.?\d*$/.test(raw)) {
      setErrorKey("maxScoreInvalidInput");
      return;
    }

    setDraft(raw);

    if (isPartialInput(raw)) {
      setErrorKey(null);
      return;
    }

    const points = parseFloat(raw);
    if (isNaN(points)) {
      setErrorKey(null);
      return;
    }

    if (points < 0 || (hasValidMax && points > maxNum)) {
      setErrorKey("absoluteScoreRange");
      return;
    }

    setErrorKey(null);
    props.h(points.toString());
  };

  return (
    <div className="field">
      <label className="label" htmlFor="totalscore">
        <div>
          {t("totalScore") + " (" + t("ABSOLUTE") + ")"}
          <Tooltip
            icon="circle-info"
            class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
            content={t("absolutescoreTooltip")}
          ></Tooltip>
        </div>
      </label>
      <div className="field has-addons mb-0">
        <p className="control is-expanded">
          <input
            type="text"
            className="input"
            value={draft}
            onChange={onChange}
            disabled={inputBlocked}
            pattern="^[+\-]?\d*[.,]?\d*$"
          />
        </p>
        {hasValidMax && (
          <p className="control">
            <span className="button is-static">{t("ofMaxPoints", { max: maxNum })}</span>
          </p>
        )}
      </div>
      {inputBlocked && <p className="help">{t("totalScoreNoMax")}</p>}
      {errorKey && <p className="help is-danger">{t(errorKey, { max: maxNum })}</p>}
    </div>
  );
};
